<template>
    <div class="home">
        <div class="top-white-box"></div>
        <template v-if="isLoading">
            <div class="article-banner-section" v-if="!isClean">
                <div class="shimmer-placeholder article-banner-shimmer"></div>
            </div>
            <div class="article-description-section" v-if="!isClean">
                <div class="shimmer-placeholder article-description-shimmer"></div>
            </div>
            <div class="article-deals-container">
                <Deals :listOfDeals="null" :showPrice="true"></Deals>
            </div>
        </template>
        <template v-else>
            <!-- Banner Image (if available) -->
            <div v-if="articleDetails && articleDetails.imgUrl && !isClean" class="article-banner-section">
                <img
                    :src="articleDetails.imgUrl"
                    alt="Banner"
                    class="article-banner-image"
                />
            </div>

            <!-- Article Description (expandable) -->
            <div v-if="articleDetails && articleDetails.desc && !isClean" class="article-description-section">
                <div class="article-description-content-wrapper" :class="{ 'expanded': isDescriptionExpanded }" @click="toggleDescription">
                    <div class="article-description-content" v-html="articleDetails.desc"></div>
                    <div v-if="!isDescriptionExpanded" class="article-description-fade">
                        <div class="article-description-expand-message">
                            <span class="expand-message-text">▼ Read More</span>
                        </div>
                    </div>
                    <div v-if="isDescriptionExpanded" class="article-description-collapse">
                        <span class="material-symbols-outlined article-description-icon expanded">
                            expand_less
                        </span>
                    </div>
                </div>
            </div>

            <!-- Last Updated Date -->
            <div v-if="articleDetails && articleDetails.date && !isClean" class="last-updated-section">
                <div class="last-updated-content">
                    <span class="material-symbols-outlined last-updated-icon">schedule</span>
                    <div class="last-updated-text">
                        <span class="last-updated-label">Last updated:</span>
                        <span class="last-updated-date">{{ formatLastUpdatedDate(articleDetails.date) }}</span>
                        <span v-if="getTimeAgo(articleDetails.date)" class="time-ago-text">({{ getTimeAgo(articleDetails.date) }})</span>
                    </div>
                </div>
            </div>

            <div v-if="articleDetails" class="article-deals-container">
                <Deals :listOfDeals="articleDetails?.deals || []" :showPrice="true"></Deals>
            </div>
        </template>
        <div class="top-gray-box" v-if="articleDetails && !isClean">
            <h1>Other Posts You Might Like</h1>
        </div>
        <Articles :excludeArticles="Number($route.params.id)" v-if="articleDetails && !isClean" />        
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/api'
import settings from '@/settings'
import { Article } from '@/classes/Articles'
import Articles from "@/components/Articles.vue"
import Deals from "@/components/Deals.vue"
import Cookies from 'js-cookie'

export default defineComponent({
    name: "articledetails",
    components: {
        Articles,
        Deals
    },
    setup() {
        const route = useRoute()
        const isParticipant = ref(false)
        const isLoading = ref(false)
        const articleDetails = ref<Article | null>(null)
        const articleUrl = ref<string | null>(null)
        const isClean = ref(false)
        const logoUrl = ref(settings.logoUrl)
        const isDescriptionExpanded = ref(false)

        const formatDate = (date: Date, format: string): string => {
            // You'll need to implement or import a date formatting function
            // This is just a placeholder
            return new Date(date).toLocaleDateString()
        }

        const formatLastUpdatedDate = (date: Date | string): string => {
            const dateObj = typeof date === 'string' ? new Date(date) : date;
            if (!dateObj || isNaN(dateObj.getTime())) return '';
            
            // Format as "Sep 23, 2025 • 10:42 AM"
            const dateStr = dateObj.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric'
            });
            
            const timeStr = dateObj.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
            
            return `${dateStr} • ${timeStr}`;
        };

        const getTimeAgo = (date: Date | string): string => {
            const dateObj = typeof date === 'string' ? new Date(date) : date;
            if (!dateObj || isNaN(dateObj.getTime())) return '';
            
            const now = new Date();
            const diffMs = now.getTime() - dateObj.getTime();
            const diffSeconds = Math.floor(diffMs / 1000);
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);
            
            if (diffSeconds < 60) {
                return 'just now';
            } else if (diffMins < 60) {
                return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
            } else if (diffHours < 24) {
                return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
            } else if (diffDays < 7) {
                return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
            } else {
                return '';
            }
        };

        const toggleDescription = () => {
            const wasExpanded = isDescriptionExpanded.value
            isDescriptionExpanded.value = !isDescriptionExpanded.value
            
            // If we're closing the description, scroll to top
            if (wasExpanded) {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                })
            }
        }

        const init = async () => {
            isLoading.value = true
            const articleId = Number(route.params.id)
            articleDetails.value = null
            console.log('articleId', articleId)
            try {
                const response = await api.getArticleDetails(articleId, true)
                // Convert to Article instance to ensure proper property mapping
                const articleDetailsTemp = new Article(response.data);
                if(articleDetailsTemp.imgUrl === null) articleDetailsTemp.imgUrl = settings.logoUrl
                articleDetails.value = articleDetailsTemp
                document.title = articleDetails.value.pageTitle + ' | ' + settings.siteNameCaps
            } catch (error) {
                console.error('Error fetching article details:', error)
            } finally {
                isLoading.value = false
            }
        }

        onMounted(() => {
            //use js cookie to get the cookie
            const cookie_lastref = Cookies.get('MMD_ParticipantRefCode')
            if (cookie_lastref) {
                isParticipant.value = true
            }

            isClean.value = !!route.query.direct
            init()
        })

        return {
            isParticipant,
            isLoading,
            articleDetails,
            articleUrl,
            isClean,
            formatDate,
            formatLastUpdatedDate,
            getTimeAgo,
            isDescriptionExpanded,
            toggleDescription
        }
    }
})
</script>

<style scoped>
.shimmer-placeholder {
    animation-duration: 1s;
    animation-fill-mode: forwards;
    animation-iteration-count: infinite;
    animation-name: placeHolderShimmer;
    animation-timing-function: linear;
    background: #f6f7f9;
    background-image: linear-gradient(
        to right,
        #f6f7f9 0%,
        #e9ebee 20%,
        #f6f7f9 40%,
        #f6f7f9 100%
    );
    background-repeat: no-repeat;
    background-size: 100%;
}

.article-banner-shimmer {
    width: 100%;
    height: 300px;
    border-radius: 8px;
}

.article-description-shimmer {
    width: 100%;
    height: 200px;
    border-radius: 8px;
}

@keyframes placeHolderShimmer {
    0% {
        background-position: -468px 0;
    }
    100% {
        background-position: 468px 0;
    }
}

/* Article Banner Section */
.article-banner-section {
    max-width: 1200px;
    margin: 20px auto;
    padding: 0;
    overflow: hidden;
    border-radius: 8px;
}

@media (min-width: 1440px) {
    .article-banner-section {
        max-width: 1400px;
    }
}

.article-banner-image {
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
    aspect-ratio: 21 / 9;
    border-radius: 8px;
}

@media (max-width: 767px) {
    .article-banner-section {
        margin: 0;
        border-radius: 0;
    }
    
    .article-banner-image {
        border-radius: 0;
    }
}

/* Article Description Section */
.article-description-section {
    max-width: 1200px;
    margin: 20px auto;
    padding: 0;
    background: #fafafa;
    overflow: hidden;
    border-radius: 8px;
}

@media (min-width: 1440px) {
    .article-description-section {
        max-width: 1400px;
    }
}

.article-description-content-wrapper {
    position: relative;
    overflow: hidden;
    transition: max-height 0.4s ease;
    cursor: pointer;
}

.article-description-content-wrapper:not(.expanded) {
    max-height: 200px;
}

.article-description-content-wrapper.expanded {
    max-height: 5000px;
}

.article-description-content {
    padding: 15px 20px 20px 20px;
    color: #555;
    line-height: 1.7;
    font-size: 1em;
    pointer-events: none;
    text-align: left;
}

.article-description-fade {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 90px;
    background: linear-gradient(to bottom, rgba(250, 250, 250, 0) 0%, rgba(250, 250, 250, 0.85) 70%, rgba(250, 250, 250, 1) 100%);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding-bottom: 15px;
    pointer-events: none;
}

.article-description-expand-message {
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;
    cursor: pointer;
}

.expand-message-text {
    font-size: 14px;
    font-weight: 600;
    color: #667eea;
    padding: 10px 16px;
    background: white;
    border-radius: 24px;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
    transition: all 0.3s ease;
    text-align: center;
    white-space: nowrap;
}

.article-description-content-wrapper:hover .expand-message-text {
    background: #667eea;
    color: white;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    transform: translateY(-3px) scale(1.02);
}

.article-description-collapse {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}

.article-description-collapse .article-description-icon {
    font-size: 36px;
    color: #667eea;
    background: white;
    border-radius: 50%;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
    transition: all 0.3s ease;
    pointer-events: auto;
    cursor: pointer;
}

.article-description-content-wrapper:hover .article-description-collapse .article-description-icon {
    background: #667eea;
    color: white;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    transform: translateY(3px) scale(1.05);
}

.article-description-content :deep(p) {
    margin: 0 0 12px 0;
}

.article-description-content :deep(p:last-child) {
    margin-bottom: 0;
}

.article-description-content :deep(img) {
    max-width: 100%;
    height: auto;
    border-radius: 6px;
    margin: 12px 0;
}

.article-description-content :deep(a) {
    color: #ff5e8a;
    text-decoration: none;
    font-weight: 500;
}

.article-description-content :deep(a:hover) {
    text-decoration: underline;
}

.article-description-content :deep(ul),
.article-description-content :deep(ol) {
    margin: 12px 0;
    padding-left: 24px;
}

.article-description-content :deep(li) {
    margin: 6px 0;
}

.article-description-content :deep(h1),
.article-description-content :deep(h2),
.article-description-content :deep(h3),
.article-description-content :deep(h4),
.article-description-content :deep(h5),
.article-description-content :deep(h6) {
    margin: 16px 0 12px 0;
    color: #333;
    font-weight: 600;
}

.article-description-content :deep(h1) { font-size: 1.8em; margin-top: 0; }
.article-description-content :deep(h2) { font-size: 1.5em; margin-top: 0; }
.article-description-content :deep(h3) { font-size: 1.3em; margin-top: 0; }
.article-description-content :deep(h4) { font-size: 1.1em; margin-top: 0; }

@media (max-width: 767px) {
    .article-description-section {
        margin: 0;
        padding: 0;
        max-width: 100%;
        width: 100%;
    }
    
    .article-description-content-wrapper:not(.expanded) {
        max-height: 110px;
    }
    
    .article-description-content {
        padding: 12px 15px 18px 15px;
        font-size: 0.95em;
    }
    
    .article-description-fade {
        height: 70px;
        padding-bottom: 12px;
    }
    
    .expand-message-text {
        font-size: 12px;
        padding: 8px 12px;
    }
    
    .article-description-collapse {
        bottom: 15px;
    }
    
    .article-description-collapse .article-description-icon {
        font-size: 32px;
        width: 44px;
        height: 44px;
    }
}

/* Last Updated Section */
.last-updated-section {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 20px auto;
    max-width: 1200px;
}

@media (min-width: 1440px) {
    .last-updated-section {
        max-width: 1400px;
    }
}

@media (max-width: 768px) {
    .last-updated-section {
        margin-top: 12px;
        margin-bottom: 12px;
    }
}

.last-updated-content {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: transparent;
    border: none;
    border-radius: 8px;
    box-shadow: none;
}

.last-updated-icon {
    font-size: 15px;
    color: #9ca3af;
    flex-shrink: 0;
    opacity: 0.7;
}

.last-updated-text {
    display: flex;
    flex-direction: row;
    align-items: baseline;
    gap: 6px;
    line-height: 1.3;
    flex-wrap: wrap;
}

.last-updated-label {
    font-size: 0.75em;
    color: #9ca3af;
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
    white-space: nowrap;
}

.last-updated-date {
    font-size: 0.85em;
    color: #6b7280;
    font-weight: 400;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    white-space: nowrap;
}

.time-ago-text {
    font-size: 0.75em;
    color: #9ca3af;
    font-weight: 400;
    white-space: nowrap;
    opacity: 0.8;
}
</style>
